import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { OperationError, issueTextOrDefault } from "../DAO";
import { User } from "./User";
import { login } from "./AuthDAO";
import { DocumentTitle } from "../DocumentTitle";

interface FormFields {
   username: string;
   password: string;
}
type FieldName = 'username' | 'password';

interface SubmissionState {
   isSubmitting: boolean,
   errorMessage?: string
}

interface Props {
   setUser: (user: User) => void;
}
export const LoginForm = ({ setUser }: Props): JSX.Element => {
   const { register, formState: { errors }, handleSubmit, setValue } = useForm<FormFields>();

   function trimOnBlur(fieldName: FieldName) {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
         const value = e.target.value;
         const trimmed = value.trim();
         if (value !== trimmed) {
            setValue(fieldName, e.target.value.trim(), { shouldValidate: true });
         }
      };
   }

   const [submissionState, setSubmissionState] = useState<SubmissionState>({ isSubmitting: false });
   function submit({ username, password }: FormFields): void {
      if (username && password) {
         setSubmissionState({ isSubmitting: true });
         login(username, password)
            .then(setUser)
            .then(() => setSubmissionState({ isSubmitting: false }))
            .catch((e: OperationError) => {
               const mainIssue = e.issues[0];
               const msg = mainIssue.code === 'UNAUTHORIZED' ?
                  "Invalid username / password" :
                  issueTextOrDefault(mainIssue);
               setSubmissionState({
                  isSubmitting: false,
                  errorMessage: msg
               })
            });
      }
   }

   function clearSubmissionError(): void {
      setSubmissionState({
         isSubmitting: submissionState.isSubmitting
      });
   }

   const htmlId = (suffix?: string): string => {
      const prefix = "page-login";
      return suffix ? prefix + "_" + suffix : prefix;
   }
   return (<>
      <DocumentTitle title="Log In"></DocumentTitle>
      <article id={htmlId()}>
         <form onSubmit={handleSubmit(submit)}>
            <div>
               <label htmlFor={htmlId("username")}>Username:</label>
               <input id={htmlId("username")} type="text" spellCheck={false}
                  {...register("username", {
                     required: "Username is required",
                     onBlur: trimOnBlur("username"),
                     onChange: clearSubmissionError
                  })}
                  disabled={submissionState.isSubmitting}
                  className={errors.username ? "error" : ""} />
               {errors.username &&
                  <label htmlFor={htmlId("username")} className="error">{errors.username.message}</label>}
            </div>
            <div>
               <label htmlFor={htmlId("password")}>Password:</label>
               <input id={htmlId("password")} type="password" spellCheck={false}
                  {...register("password", {
                     required: "Password is required",
                     onBlur: trimOnBlur("password"),
                     onChange: clearSubmissionError
                  })}
                  disabled={submissionState.isSubmitting}
                  className={errors.password ? "error" : ""} />
               {errors.password &&
                  <label htmlFor={htmlId("username")} className="error">{errors.password.message}</label>}
            </div>
            {submissionState.isSubmitting &&
               <div>Connecting...</div>}
            {submissionState.errorMessage &&
               <div><label className="error">{submissionState.errorMessage}</label></div>}
            <button type="submit" disabled={submissionState.isSubmitting}>Log in</button>
         </form>
      </article>
   </>);
};
